/**
 * The credential vocabulary (ADR-051), read by the backend when it mints and verifies a token, and
 * by the surfaces that list what holds a person's authority.
 *
 * `access-control.ts` decides what a *subject* may do. This decides what a *token* may do, which is
 * not the same question: a credential carries a subset of its holder's authority, never the whole of
 * it, and never more. Scopes are drawn from `PERMISSIONS` rather than from a list of their own —
 * a parallel vocabulary drifts from the original the first week somebody adds a permission and
 * forgets the mirror.
 */

import { ENFORCED_PERMISSIONS, PERMISSIONS, type PermissionType } from './access-control.js';
import { ASIF_CEILING_ALLOWED } from './as-if-ceiling.js';

/**
 * What a credential is for. A closed set, because free-form scope strings put the security decision
 * at every call site that mints a token; a kind puts it in one reviewable place.
 */
export const CREDENTIAL_KINDS = [
  'session',
  'bridge',
  'agent',
  'integration',
  'verification',
  // F-0709: a person's own view of one work area as another role. Minted only from a `session`; its
  // user stays the real person, and it holds `holder ∩ target ∩ ceiling` in the one work area it is
  // bound to (`docs/concept-start-a-work-area-as-if.md` §2.2).
  'as-if',
] as const;
export type CredentialKindType = (typeof CREDENTIAL_KINDS)[number];

/**
 * The bridge holds a credential on a developer's laptop, started with the machine and outliving any
 * particular piece of work (ADR-050). It files and annotates; it does not administer. Append-only by
 * construction: no `work_item.update`, no `work_item.delete`, nothing work-area scoped.
 *
 * Written out rather than derived from a role, because this set answers "what may a background
 * process on a laptop do" and a role answers "what may a person do" — the day those two diverge, a
 * derived ceiling would follow the role and widen without anybody deciding to widen it.
 */
const BRIDGE_CEILING = [
  'work_item.read',
  'work_item.create',
  'work_item.comment',
  'work_item.attach',
  // F-0188: token usage of this machine's sessions. Report only — never `agent_usage.read`, because a
  // background process on a laptop holding a read of anybody's usage is authority nobody asked for.
  'agent_usage.report',
  // F-0694 (SCH-3, the captain's ruling): the governed jobs this machine holds and the runs its engine
  // recorded. Report only, as `agent_usage.report` is — never `schedule.manage` or `schedule.run`: a
  // background process on a laptop defines and starts nothing.
  'schedule.report',
] as const satisfies readonly PermissionType[];

/**
 * The maximum a `verification` credential may ever carry (I-0326, `concept-independent-verification-
 * access.md` §3.1) — the five permissions that read without writing anything, and nothing else.
 * `work_area.export` is deliberately excluded even here: a bulk extract is a data-exfiltration-shaped
 * act, not "what the screen shows," and no verification run needs it.
 *
 * `role.read` is in the CEILING — the maximum a mint could ever declare — but is left out of
 * `VERIFICATION_DEFAULT_DECLARED_SCOPE` below, because it gates the permission/admin screens
 * (`PERMISSION_ADMIN_UI`) and an ordinary work-item verification run has no use for it. Whether a
 * mint should ever be allowed to declare it is left open (concept §7); nothing in this codebase mints
 * one that does yet.
 */
const VERIFICATION_CEILING = [
  'work_item.read',
  'release.read',
  'agent.read',
  'work_area.read',
  'role.read',
] as const satisfies readonly PermissionType[];

/**
 * The maximum an `agent` credential may ever carry (I-0610, AC-5).
 *
 * ⚠️ **A judgement call, flagged rather than picked silently** — ADR-051 §2 describes the ceiling as
 * "what the agent's own bindings allow," which is a *dynamic*, per-agent fact that
 * `AccessControlService.narrowToOwner` already enforces at the binding level (I-0610). This constant
 * is a different, *static* thing: the class of actions any agent credential may ever be handed,
 * whatever bindings a particular agent later holds — the second, independent lock AC-5 asks for
 * ("the bindings path and the ceiling must each fail the request on their own").
 *
 * Drawn from what an agent does today per the concept this item closes the hole for: read and write
 * the work it is doing (`work_item.*` short of `delete`), read the release it ships into, read the
 * agent roster, and answer what ADR-097 says only the responsible subject may answer.
 *
 * `release.manage` since I-0727: D-0051's answer note (Jay, 2026-09-22) gives a signed-in connector
 * "everything needed for release management", and an OAuth grant freezes this ceiling as its scope,
 * exactly as `featit agent-credential connect` does. Held only where the agent's own binding grants it
 * (`project-agent` carries it too) and where its owner does. Excluded on
 * purpose: `work_item.delete` (destructive), `work_area.*` and `role.*` and `policy.manage` and
 * `view.manage` (administrative), `agent.create`/`agent.approve` (identity management — approving
 * or reshaping the roster is a person's call), `verification.mint`, and `agent_usage.configure`
 * (the interval is an installation's setting). `agent.merge` is not in `PERMISSIONS` at all
 * (I-0136 consequence 2) — merging one's own agents is gated on ownership alone, so there is
 * nothing for this ceiling to exclude it from.
 *
 * `agent_usage.report` since D-0137 (Jay, 2026-09-27; ADR-138): a runner with no bridge on its
 * machine — CI, a server, a cloud agent — reports its own sessions' presence and token usage with its
 * agent key, and only its own (`AgentReportsService`). A credential's scope is frozen at mint, so keys
 * minted before this do not carry it and are re-created.
 *
 * `part.manage` since D-0207 (Jay, 2026-09-29: "we have separate permissions for editing the tree,
 * but everyone gets it by default unless it was removed in the respective role"). This widens the
 * static cap, and it is safe for three reasons that do not hold for the administrative permissions
 * excluded above: **the part tree is structure, not access** — no tree edit changes who may see or
 * do anything, so an agent holding it cannot widen its own or anybody's authority (ADR-051's rule,
 * that a credential carries less than its holder, is untouched: `narrowToOwner` still caps it at the
 * owner's own `part.manage`, and a role it was removed from grants it to nobody); **every change is
 * recorded** in the part's history (`part_events`, append-only) with the agent's name; and **every
 * change is reversible** — a rename, move or merge keeps the part's identity, its items and its old
 * handle, and a retired part reopens. A credential's scope is frozen at mint, so agent keys and
 * connector grants minted before this do not carry it and are re-created to edit the tree.
 */
const AGENT_CEILING = [
  // F-0617: upper bound only; existing minted scopes and bindings are unchanged.
  'file_review.read',
  'file_review.contribute',
  'file_review.manage',
  'work_item.read',
  'work_item.create',
  'work_item.update',
  'work_item.comment',
  'work_item.attach',
  'release.read',
  'release.manage',
  // F-0422: an agent defines and retires the schedules its work area's jobs check in to, where its
  // binding and its owner hold the permission (`project-agent` carries it).
  'schedule.manage',
  'schedule.run',
  'work_area.read',
  'agent.read',
  'decision.answer',
  'agent_usage.report',
  'part.manage',
  // F-0542 (access control §23): an agent's own credential carries what its subagents may write,
  // so a subagent working under an agent key is asked the same two permissions.
  'subagent.comment',
  'subagent.update_held',
  'subagent.close_as_verifier',
] as const satisfies readonly PermissionType[];

/**
 * F-0426 SW-35g: what a scheduled run's own key may do, frozen into its credential at mint. The agent
 * ceiling without `schedule.manage`: a run does its job and checks in; defining, changing or retiring
 * its schedule stays a person's (or the owning agent's) act with its own credential. Widening it is a
 * change to this line and to the criterion, which lists the set.
 */
export const SCHEDULE_RUN_CREDENTIAL_SCOPE: readonly PermissionType[] = AGENT_CEILING.filter(
  (permission) => permission !== 'schedule.manage' && permission !== 'schedule.run',
);

/**
 * The address domain of a verification credential's disposable subject (I-0350). Nothing signs in
 * with it and nothing mails it; it marks a user row as a subject rather than a person, which is what
 * keeps it out of people listings while its credential lives and lets the sweep find it after.
 */
export const VERIFICATION_SUBJECT_EMAIL_DOMAIN = 'disposable.featit.internal';

export function isVerificationSubjectEmail(email: string): boolean {
  const normalised = email.trim().toLowerCase();
  return (
    normalised.startsWith('verification+') &&
    normalised.endsWith(`@${VERIFICATION_SUBJECT_EMAIL_DOMAIN}`)
  );
}

/**
 * What a `verification` credential declares by default when nobody asks for more (concept §3.1).
 *
 * Frozen into the credential's `scope` column at mint time, never left `null` — see
 * `VerificationService.mint` and AC-C of the concept. A `null` scope would resolve to "the kind's
 * current ceiling," which is exactly the widening-on-a-future-permission-add this constant exists to
 * avoid (concept §4.3).
 */
export const VERIFICATION_DEFAULT_DECLARED_SCOPE = [
  'work_item.read',
  'release.read',
  'agent.read',
  'work_area.read',
] as const satisfies readonly PermissionType[];

/**
 * The maximum a credential of each kind may carry — a cap, never a grant.
 *
 * `session` and `integration` are the whole vocabulary on purpose, and each for its own reason. A
 * `session` is a person signed in, bounded by what that person holds. An `integration` declares its
 * scope per credential at creation and cannot widen afterwards, so the bound is the declaration, not
 * the kind. `bridge`, `agent` and `verification` are narrowed by their kind, because each is one job
 * known in advance — a background filer, a named agent doing the work it is bound to, and a
 * read-only third-party viewer.
 *
 * ⭐ **`agent` is narrowed to `AGENT_CEILING` as of I-0610 (AC-5).** It used to sit at the whole
 * vocabulary alongside `session`, which was correct only as long as nothing minted a `kind: 'agent'`
 * credential — the same reasoning `ACCEPTED_CREDENTIAL_KINDS` in `credential-claims.ts` gives for
 * refusing a kind nothing issues yet. Once a credential *is* that kind, its own bindings are bounded
 * by `AccessControlService.narrowToOwner` (a dynamic, per-agent bound), and the ceiling here is the
 * second, static bound — the class of actions any agent credential may ever be handed regardless of
 * which agent it is. Defence in depth: either one failing on its own has to refuse the request.
 *
 * Typed as a total `Record` so adding a kind without deciding its ceiling does not compile. Each
 * literal ceiling carries `satisfies readonly PermissionType[]`, so retiring a permission from
 * `PERMISSIONS` breaks the build here rather than leaving a ceiling that caps a string nobody grants.
 */
export const CREDENTIAL_KIND_CEILINGS: Record<CredentialKindType, readonly PermissionType[]> = {
  session: PERMISSIONS,
  bridge: BRIDGE_CEILING,
  agent: AGENT_CEILING,
  integration: PERMISSIONS,
  verification: VERIFICATION_CEILING,
  // F-0709: the most an as-if credential could ever hold, the vocabulary less `ASIF_CEILING_DENIED`. The
  // mint refuses an as-if credential that asks to start another, so `as_if.start` is never used.
  'as-if': ASIF_CEILING_ALLOWED,
};

/**
 * Which surface a credential is for, checked as `aud`. Without it "scoped" is a label rather than a
 * boundary: a stolen bridge token could be replayed anywhere the bearer scheme is accepted.
 *
 * The desktop is deliberately absent. It renders the web app against a deployment (ADR-050) rather
 * than being a surface of its own, and an audience that names a shell instead of a boundary is one
 * more value to check without one more thing being checked.
 *
 * `agent` joins the list as of I-0610: the surface an agent credential is minted for, paired with
 * `kind: 'agent'` the same way `bridge` pairs `aud: 'bridge'` with `kind: 'bridge'` — both have to
 * widen for either to admit anything, because `ENTRY_POINT_AUDIENCES` in `credential-claims.ts` is
 * the interlock that actually holds.
 */
export const CREDENTIAL_AUDIENCES = [
  'web',
  'mobile',
  'cli',
  'bridge',
  'integration',
  'agent',
] as const;
export type CredentialAudienceType = (typeof CREDENTIAL_AUDIENCES)[number];

/**
 * What the credential list calls a credential before anybody renames it (ADR-051 §5).
 *
 * A label is what a person reads when deciding whether to revoke something, so an unlabelled
 * credential is one nobody dares touch. Total over the audiences so adding one forces a name for it.
 *
 * Here rather than beside the mint, because two readers need the same answer: the backend stamps it
 * at mint time, and every surface listing credentials has to supply it for the records minted before
 * the column existed. A second copy would give one credential two names depending on its age.
 */
export const DEFAULT_CREDENTIAL_LABELS: Record<CredentialAudienceType, string> = {
  web: 'Browser session',
  mobile: 'Mobile app',
  cli: 'FEATit CLI',
  bridge: 'Harness bridge',
  integration: 'Integration',
  agent: 'Agent credential',
};

/**
 * How long a label a person may give a credential.
 *
 * A cap rather than the column's `text`, because the label is rendered in a list beside four other
 * facts, and one pasted paragraph would push every credential below it off the screen. Shared so the
 * field that accepts it and the endpoint that stores it agree on the same number.
 */
export const CREDENTIAL_LABEL_MAX_LENGTH = 80;

/**
 * The label a `verification` credential gets when nobody names one.
 *
 * Not a `DEFAULT_CREDENTIAL_LABELS` entry: that record is keyed by *audience*, and a `verification`
 * credential's audience is `web` — the same audience an ordinary browser session declares (concept
 * §3.1, "its audience is web"). Keying the default off audience would print "Browser session" for a
 * read-only third-party link, which is the one label most likely to make an administrator skip past
 * it in a list. Kept here, beside the labels it is a sibling of, rather than folded into that record.
 */
export const DEFAULT_VERIFICATION_CREDENTIAL_LABEL = 'Read-only verification session';

/**
 * A credential as the intersection needs to see it: its kind, and the scope declared when it was
 * minted.
 *
 * `permissions: null` means the kind's full ceiling — never "grants nothing". Every credential that
 * predates ADR-051 has no declared scope, and reading absence as denial would log out the entire
 * installation on the day the migration runs. An explicitly empty array does mean "grants nothing",
 * and the two must not be conflated in either direction.
 */
export interface ICredentialScope {
  kind: CredentialKindType;
  permissions: readonly PermissionType[] | null;
}

/**
 * `effective = permissions(subject) ∩ scope(credential)` — the load-bearing rule of ADR-051,
 * evaluated per request so that both sides are current.
 *
 * The result is built by filtering the subject's permissions, which is what makes "a ceiling caps, it
 * never grants" structurally true rather than documented-as-true: nothing the subject does not hold
 * can survive a filter over what the subject holds. A `bridge` credential in the hands of a read-only
 * member still only reads, and no promotion of the holder widens the credential — widening is an
 * explicit act of minting.
 *
 * An absent credential means no narrowing, not no authority. Background jobs — the Bull processors,
 * the `pg_notify` listener — run outside any request and hold no credential at all; denying them
 * would be reading "unscoped" as "scoped to nothing".
 */
export function effectivePermissions(
  subjectPermissions: readonly PermissionType[],
  credential?: ICredentialScope | null,
): readonly PermissionType[] {
  const held = [...new Set(subjectPermissions)];
  if (!credential) return held;

  const ceiling = new Set(CREDENTIAL_KIND_CEILINGS[credential.kind]);
  // Applied as well as, not instead of, the ceiling: a scope stored years ago is not evidence that
  // the kind still permits it.
  const declared = credential.permissions === null ? ceiling : new Set(credential.permissions);

  return held.filter((permission) => ceiling.has(permission) && declared.has(permission));
}

export function isCredentialKind(value: unknown): value is CredentialKindType {
  return typeof value === 'string' && (CREDENTIAL_KINDS as readonly string[]).includes(value);
}

/** `undefined` for anything outside the vocabulary — never coerced to `session`, which is the widest. */
export function parseCredentialKind(value: unknown): CredentialKindType | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return isCredentialKind(trimmed) ? trimmed : undefined;
}

export function isCredentialAudience(value: unknown): value is CredentialAudienceType {
  return typeof value === 'string' && (CREDENTIAL_AUDIENCES as readonly string[]).includes(value);
}

/** `undefined` for anything outside the vocabulary. An unparseable audience is a failed check, not a default. */
export function parseCredentialAudience(value: unknown): CredentialAudienceType | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return isCredentialAudience(trimmed) ? trimmed : undefined;
}

/**
 * Why a refresh was refused, as a code the client can act on rather than a sentence it must parse
 * (I-0464).
 *
 * ⛔ **The distinction the codes exist for: whether the SESSION is over, or only this holder's copy
 * of the secret.** A credential is held by a store, not by a process — every tab of an origin shares
 * one `localStorage` — so a holder that reads "not valid" and clears that store signs out every
 * other holder, including the one that just refreshed successfully. That is the whole of I-0464.
 *
 * The sentence shown to a reader is written on the client, never sent in the code's place, for the
 * reason `oauthSignIn.tsx` gives: a message that crosses a URL is a message some page has to render.
 */
export const REFRESH_REFUSALS = {
  /** The chain was revoked because a secret it had rotated past came back (I-0394, ADR-096). */
  REVOKED_AFTER_REUSE: 'credential_revoked_after_reuse',
  /** The credential is revoked, expired, or its holder deactivated. The session is genuinely over. */
  SESSION_ENDED: 'session_ended',
  /**
   * No live credential holds the presented secret. ⛔ **Not a reason to end a session:** the store
   * this holder read from may already hold the secret that replaced it.
   */
  SECRET_UNKNOWN: 'refresh_secret_unknown',
  /**
   * F-0332, ADR-151 §3: the credential was paired by QR code and is bound to its device's key, and
   * the refresh carried no valid signature over a fresh nonce from `/auth/refresh/nonce`. The
   * session is not over; only its own device can renew it.
   */
  DEVICE_PROOF_REQUIRED: 'device_proof_required',
} as const;

export type RefreshRefusalType = (typeof REFRESH_REFUSALS)[keyof typeof REFRESH_REFUSALS];

/**
 * Why an access token was refused at the HTTP door, as a code an unattended client can act on
 * (I-0956).
 *
 * The codes exist for a machine client, which cannot read a sentence and ask somebody. Each one
 * calls for a different act:
 * - `ACCESS_TOKEN_EXPIRED`: renew with the refresh token.
 * - `CREDENTIAL_REVOKED` and `CREDENTIAL_EXPIRED`: a person signs in again or mints a new
 *   credential, because renewing cannot help.
 * - `NOT_ISSUED_HERE`: the client is pointed at the wrong deployment.
 * - `NOT_VALID_HERE`: the token is the wrong shape for this door.
 * - `AGENT_NOT_IN_SERVICE`: the agent was taken out of service; only a new credential, minted after
 *   it is reactivated, acts as it again.
 *
 * A missing, malformed or forged token gets no code at all.
 */
export const ACCESS_REFUSALS = {
  /** The token's own `exp` has passed. Said only after its signature verified. */
  ACCESS_TOKEN_EXPIRED: 'access_token_expired',
  /** The credential the token belongs to was revoked. */
  CREDENTIAL_REVOKED: 'credential_revoked',
  /** The credential's session reached its end. */
  CREDENTIAL_EXPIRED: 'credential_expired',
  /** No credential with this id exists on this deployment (I-0371). */
  NOT_ISSUED_HERE: 'credential_not_issued_here',
  /** The audience, purpose or kind is not one this door serves. Deliberately says no more. */
  NOT_VALID_HERE: 'credential_not_valid_here',
  /**
   * The agent the credential acts as is out of service — deactivated, merged or folded (I-1607).
   * Renewing cannot help: a person reactivates the agent and mints a new credential.
   */
  AGENT_NOT_IN_SERVICE: 'agent_not_in_service',
} as const;

export type AccessRefusalType = (typeof ACCESS_REFUSALS)[keyof typeof ACCESS_REFUSALS];

/**
 * The permissions in a kind's ceiling that no check actually consults today.
 *
 * A ceiling that caps an unenforced permission caps nothing, and a credential list that displays one
 * tells its reader something untrue — they believe they have bounded a token where no bound exists.
 * Exposed rather than left to the specification because the screens of ADR-051 §5 are the ones that
 * would otherwise make the false claim.
 */
export function unenforcedCeilingPermissions(kind: CredentialKindType): readonly PermissionType[] {
  return CREDENTIAL_KIND_CEILINGS[kind].filter(
    (permission) => !ENFORCED_PERMISSIONS.includes(permission),
  );
}

/**
 * Where the web app keeps a signed-in session: its `localStorage` slots for the built-in deployment
 * (a chosen deployment address suffixes them with its scope — `web/…/deploymentAddress.ts`). Here so
 * a tool that hands a browser a session (T-0581; since I-1804 `scripts/dev-qa-identity.sh
 * --with-web-sessions`, in memory) builds the very keys the web reads, not a copy of them.
 */
/**
 * The code of the 403 a verification credential gets for any write it was not listed for (I-2189
 * siblings, `backend/src/auth/verification/verification-write-gate.ts`). A client says it from this code,
 * in the reader's language, instead of showing the server's English sentence.
 */
export const VERIFICATION_READ_ONLY_CODE = 'verification-read-only';

/**
 * I-2439: the codes of the refusals a signed-out door answers, each with its English `message`. A
 * client says a refusal from its code, in the reader's language, and shows the server's sentence only
 * for a code it does not know. A link that was spent, expired or never issued reads the same (one
 * sentence per door, so a refusal tells nobody which of the three it was).
 */
export const SIGNED_OUT_REFUSALS = {
  /** `POST /auth/email-verification/confirm`. */
  VERIFICATION_LINK: 'verification-link-invalid',
  /** `POST /auth/password-reset/confirm`. */
  PASSWORD_RESET_LINK: 'password-reset-link-invalid',
  /** The reset's review of recently added sign-in methods (T-0633). */
  PASSWORD_RESET_REVIEW: 'password-reset-review-ended',
  /** A sign-in address change's confirm and undo links. */
  SIGN_IN_ADDRESS_LINK: 'sign-in-address-link-invalid',
  /** `POST /mail/unsubscribe`: a token that names no message (IN-103). */
  UNSUBSCRIBE_LINK: 'unsubscribe-link-unknown',
  /** First-run setup (`backend/src/setup`): a setup token unknown, expired or spent. */
  SETUP_TOKEN: 'setup-token-refused',
  /** First-run setup, once the deployment has an administrator. */
  SETUP_NOT_NEEDED: 'setup-not-needed',
  /** First-run setup naming a deactivated account. */
  SETUP_ACCOUNT_DEACTIVATED: 'setup-account-deactivated',
  /** First-run setup naming an existing account without proving its password. */
  SETUP_WRONG_PASSWORD: 'setup-address-wrong-password',
  /** A setup door that proves nothing about an existing account, naming its address. */
  SETUP_ADDRESS_TAKEN: 'setup-address-taken',
  /** A setup door given no usable address: 400. */
  SETUP_ADDRESS_INVALID: 'setup-address-invalid',
  /** A bootstrapped administrator's claim link, unknown, expired or spent. */
  CLAIM_LINK: 'claim-link-refused',
  /** A door that sends mail (sign-up, a reset, a new link) on a deployment with no mailer: 503. */
  MAIL_NOT_SET_UP: 'mail-not-set-up',
  /** The same door when the mail provider would not take the message: 502. */
  MAIL_NOT_ACCEPTED: 'mail-not-accepted',
  /** A password sign-in with a wrong address or password: one answer for both halves. */
  INVALID_CREDENTIALS: 'invalid-credentials',
  /** A passkey sign-in naming a passkey that is not this account's, or an account that is gone (IN-106c). */
  PASSKEY_NOT_VALID: 'passkey-not-valid',
  /** A passkey sign-in whose signature could not be verified; the verifier's detail stays in the English. */
  PASSKEY_NOT_VERIFIED: 'passkey-not-verified',
  /** Sign-up (`POST /auth/register`) with an address that already has an account (IN-106d): 409. */
  SIGN_UP_ADDRESS_TAKEN: 'sign-up-address-taken',
  /** Sign-up with no address, typed or from the invitation: 400. */
  SIGN_UP_ADDRESS_MISSING: 'sign-up-address-missing',
} as const;

export type SignedOutRefusalCodeType =
  (typeof SIGNED_OUT_REFUSALS)[keyof typeof SIGNED_OUT_REFUSALS];

export function isSignedOutRefusalCode(value: unknown): value is SignedOutRefusalCodeType {
  return Object.values<string>(SIGNED_OUT_REFUSALS).includes(value as string);
}

export const WEB_ACCESS_TOKEN_SLOT = 'featit:accessToken';
export const WEB_REFRESH_TOKEN_SLOT = 'featit:refreshToken';
